import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Gamepad2, Loader2 } from "lucide-react";
import { StartGameDialog } from "./StartGameDialog";
import { EndGameDialog } from "./EndGameDialog";
import {
  GAME_TYPE,
  GameSession,
  useStartGame,
  useEndGame,
} from "@/lib/api/games";
import { useToast } from "@/components/ui/use-toast";

interface GameControlButtonProps {
  mentorshipId: string;
  sendbirdChannelUrl: string;
  activeGame: GameSession | null;
}

export function GameControlButton({
  mentorshipId,
  sendbirdChannelUrl,
  activeGame,
}: GameControlButtonProps) {
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const { toast } = useToast();

  const startGameMutation = useStartGame();
  const endGameMutation = useEndGame();

  const handleStartGame = async (gameType: GAME_TYPE) => {
    setShowStartDialog(false);
    try {
      await startGameMutation.mutateAsync({
        game_type: gameType,
        mentorship_id: mentorshipId,
        sendbird_channel_url: sendbirdChannelUrl,
      });

      toast({
        title: "Game Started!",
        description: "Your game has been started successfully.",
      });
    } catch (error) {
      console.error("Failed to start game:", error);
      toast({
        title: "Error",
        description: "Failed to start game. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEndGame = async (score: number) => {
    if (!activeGame) return;

    setShowEndDialog(false);
    try {
      await endGameMutation.mutateAsync({
        game_session_id: activeGame.id,
        final_score: score,
      });

      toast({
        title: "Game Complete!",
        description: `You finished with a score of ${score}!`,
      });
    } catch (error) {
      console.error("Failed to end game:", error);
      toast({
        title: "Error",
        description: "Failed to end game. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isLoading = startGameMutation.isPending || endGameMutation.isPending;

  return (
    <>
      <Button
        variant={activeGame ? "default" : "outline"}
        size="sm"
        onClick={() => {
          if (activeGame) {
            setShowEndDialog(true);
          } else {
            setShowStartDialog(true);
          }
        }}
        disabled={isLoading}
        className={
          activeGame
            ? "bg-green-600 hover:bg-green-700"
            : "border-blue-600 text-blue-600 hover:bg-blue-50"
        }
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Gamepad2 className="mr-2 h-4 w-4" />
        )}
        {activeGame ? "End Game" : "Start Game"}
      </Button>

      <StartGameDialog
        open={showStartDialog}
        onOpenChange={setShowStartDialog}
        onStartGame={handleStartGame}
        isLoading={startGameMutation.isPending}
      />

      <EndGameDialog
        open={showEndDialog}
        onOpenChange={setShowEndDialog}
        onEndGame={handleEndGame}
        activeGame={activeGame}
        isLoading={endGameMutation.isPending}
      />
    </>
  );
}
